import { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { Panel, Empty } from "@/components/PageHeader";
import { StorePicker } from "@/components/flyers/FlyerAnalysis";
import { tone } from "@/components/flyers/ProductTable";
import { ProductPicker } from "@/components/products/ProductPicker";
import { CustomersTable, LostTable, ProductsTable, StoresTable } from "@/components/products/ProductTables";
import { ProductAi, SavedLists, SegmentButton, SegmentDialog } from "@/components/products/ProductExtras";
import { api, eur, errMsg, fdate } from "@/lib/api";
import { downloadCsv, nfmt, pctFmt } from "@/lib/csv";
import { useAuth } from "@/context/AuthContext";

const iso = (d) => d.toISOString().slice(0, 10);

const Card = ({ id, label, value, delta, cmp }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`pa-kpi-${id}`}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1.5 font-heading text-xl font-semibold text-slate-900" data-testid={`pa-kpi-${id}-value`}>{value}</div>
    {cmp !== undefined && <div className="mt-0.5 text-[11px]"><span className={tone(delta)}>{pctFmt(delta)}</span><span className="text-slate-400"> · prima {cmp}</span></div>}
  </div>
);

const Kpis = ({ d }) => {
  const k = d.kpi, c = d.kpi_cmp, x = d.kpi_delta, cs = d.customer_summary;
  const items = [["revenue", "Valore venduto", eur], ["pieces", "Pezzi", (v) => nfmt(v, 0)], ["receipts", "Scontrini", (v) => nfmt(v, 0)], ["avg_price", "Prezzo medio", eur],
    ["promo_revenue", "Valore in promo", eur], ["promo_pieces", "Pezzi in promo", (v) => nfmt(v, 0)], ["promo_share", "Quota promo", (v) => `${nfmt(v)}%`]];
  return (<>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">{items.map(([id, l, f]) => <Card key={id} id={id} label={l} value={f(k[id])} cmp={f(c[id])} delta={x[id]} />)}</div>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="pa-customer-summary">
      <Card id="buyers" label="Clienti con carta" value={nfmt(cs.buyers, 0)} cmp={nfmt(cs.cmp_buyers, 0)} delta={cs.cmp_buyers ? Math.round(((cs.buyers - cs.cmp_buyers) / cs.cmp_buyers) * 1000) / 10 : null} />
      <Card id="new" label="Nuovi (non nel confronto)" value={nfmt(cs.new, 0)} />
      <Card id="returning" label="Ricorrenti (anche nel confronto)" value={nfmt(cs.returning, 0)} />
      <Card id="lost" label="Persi (solo nel confronto)" value={nfmt(cs.lost, 0)} />
      <Card id="repeat" label="Riacquisto nel periodo (2+ scontrini)" value={nfmt(cs.repeat, 0)} />
    </div>
    {cs.capped && <p className="text-xs text-amber-600">Molti acquirenti: i conteggi clienti considerano i primi 20.000 per valore.</p>}
  </>);
};

const weekly = (days) => {
  if (days.length <= 60) return days.map((x) => ({ ...x, label: fdate(x.day).slice(0, 6) }));
  const out = [];
  days.forEach((x, i) => {
    if (i % 7 === 0) out.push({ day: x.day, label: `sett. ${fdate(x.day).slice(0, 6)}`, pay: 0, ppay: 0, cmp_pay: 0 });
    const w = out[out.length - 1]; w.pay += x.pay; w.ppay += x.ppay; w.cmp_pay += x.cmp_pay;
  });
  return out;
};

const Daily = ({ d }) => (
  <Panel testId="pa-daily" title={d.days.length > 60 ? "Andamento settimanale" : "Andamento giornaliero"} action={<Button size="sm" variant="ghost" data-testid="pa-daily-csv"
    onClick={() => downloadCsv("andamento-prodotti", ["Giorno", "Valore", "Pezzi", "Valore promo", "Giorno confronto", "Valore confronto"], d.days.map((x) => [x.day, x.pay, x.qty, x.ppay, x.cmp_day, x.cmp_pay]))}><Download className="mr-1 h-4 w-4" />CSV</Button>}>
    <div className="h-64 p-4"><ResponsiveContainer><ComposedChart data={weekly(d.days)}>
      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" /><XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
      <Tooltip formatter={(v, n) => [eur(v), n]} /><Legend wrapperStyle={{ fontSize: 12 }} />
      <Bar dataKey="pay" name="Valore" fill="#2563eb" radius={[3, 3, 0, 0]} /><Bar dataKey="ppay" name="di cui in promo" fill="#f97316" radius={[3, 3, 0, 0]} />
      <Line dataKey="cmp_pay" name="Valore periodo di confronto" stroke="#94a3b8" strokeWidth={2} dot={false} /></ComposedChart></ResponsiveContainer></div>
  </Panel>
);

export const ProductAnalysis = () => {
  const { user } = useAuth();
  const KEY = `crm-product-analysis-${user?.tenant?.id || "default"}`;
  const canWrite = user?.permissions?.basket === "write";
  const canSeg = canWrite && user?.permissions?.segments === "write";
  const saved = JSON.parse(localStorage.getItem(KEY) || "{}");
  const today = new Date();
  const [prods, setProds] = useState(saved.prods || []);
  const [start, setStart] = useState(saved.start || iso(new Date(today.getTime() - 29 * 864e5)));
  const [end, setEnd] = useState(saved.end || iso(today));
  const [compare, setCompare] = useState(saved.compare || "prev");
  const [stores, setStores] = useState([]);
  const [opts, setOpts] = useState([]);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [seg, setSeg] = useState(null);
  const params = { eans: prods.map((p) => p.ean).join(","), start, end, compare, stores: stores.join(","), days: start && end ? Math.round((new Date(end) - new Date(start)) / 864e5) + 1 : 30 };
  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify({ prods, start, end, compare }));
    if (!prods.length || !start || !end) { setD(null); return; }
    let live = true;
    setLoading(true); setErr("");
    api.get("/products/analysis", { params: { eans: prods.map((p) => p.ean).join(","), start, end, compare, stores: stores.join(",") } })
      .then((r) => { if (!live) return; setD(r.data); if (!stores.length) setOpts(r.data.stores.map((s) => ({ id: String(s.id), name: s.name })).sort((a, b) => a.name.localeCompare(b.name))); }).catch((e) => live && setErr(errMsg(e))).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [prods, start, end, compare, stores]);
  const name = prods.length === 1 ? prods[0].name || prods[0].ean : `${prods.length}-prodotti`;
  return (
    <div className="space-y-6" data-testid="product-analysis">
      <Panel testId="pa-controls">
        <div className="grid gap-4 p-4 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-2"><ProductPicker value={prods} onChange={setProds} /><SavedLists prods={prods} onLoad={setProds} canWrite={canWrite} /></div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-500">Dal<div className="mt-1"><DatePicker testId="pa-start" value={start} max={end} onChange={setStart} /></div></label>
            <label className="text-xs text-slate-500">Al<div className="mt-1"><DatePicker testId="pa-end" value={end} min={start} onChange={setEnd} /></div></label>
            <label className="text-xs text-slate-500">Confronta con
              <Select value={compare} onValueChange={setCompare}><SelectTrigger className="mt-1" data-testid="pa-compare"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="prev">Periodo precedente di pari durata</SelectItem><SelectItem value="yoy">Stesso periodo anno prima</SelectItem></SelectContent></Select></label>
            <label className="text-xs text-slate-500">Sedi<div className="mt-1"><StorePicker options={opts} value={stores} onChange={setStores} /></div></label>
          </div>
        </div>
        {d && <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500" data-testid="pa-periods">Periodo {fdate(d.period.start)} – {fdate(d.period.end)} · confronto {fdate(d.compare.start)} – {fdate(d.compare.end)} · tutte le vendite, con e senza carta{loading && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin" />}</p>}
      </Panel>
      {!prods.length && <Panel><Empty text="Cerca e aggiungi uno o più prodotti per EAN o descrizione" /></Panel>}
      {err && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="pa-error">{err}</div>}
      {!d && loading && <div className="flex flex-col items-center gap-2 py-16 text-xs text-slate-400"><Loader2 className="h-6 w-6 animate-spin text-blue-500" />Analisi in corso: per periodi lunghi può richiedere qualche secondo</div>}
      {d && prods.length > 0 && <>
        {canSeg && <div className="flex justify-end"><SegmentButton onClick={() => setSeg("buyers")} label="Crea segmento dai clienti" /></div>}
        <Kpis d={d} />
        <Daily d={d} />
        <ProductsTable d={d} name={name} />
        <StoresTable d={d} name={name} />
        <CustomersTable d={d} name={name} />
        <LostTable d={d} name={name} action={canSeg && <SegmentButton testId="pa-lost-segment" label="Crea segmento clienti persi" onClick={(e) => { e?.stopPropagation?.(); setSeg("lost"); }} />} />
        <ProductAi d={d} canWrite={canWrite} params={(({ days, ...x }) => x)(params)} />
      </>}
      <SegmentDialog open={!!seg} group={seg} onClose={() => setSeg(null)} params={params} prods={prods} />
    </div>
  );
};
